import React from 'react';
import cx from 'classnames';
import { Link, useParams } from 'react-router-dom';
import { Input } from 'reactstrap';

import OrgTreeNodeTypeIcon from 'js/routes/Organizations/components/ManageOrg/OrgTreeVeiw/OrgTreeNodeTypeIcon';
import OrgWeightRowGutters from 'js/routes/Organizations/components/ManageOrg/OrgWeightRowGutters';
import { weightOverridesSortedForPicker } from 'js/utils/rubric';

import {
  ORG_TEAM_RUBRIC_CUSTOM_SCHEME_ID,
  flattenOrgTreeRows,
  isEditableWeightRow,
  isTreeRootWeightRow,
  isNonDefaultSchemeSelectValue,
  orgManageTeamCustomWeightsPath,
  selectValueForTeamRow,
  weightSchemeOptionValue,
} from './orgRubricSchemesUtils';
import {
  isOrgTeamRubricCustomSchemeId,
  ORG_TEAM_RUBRIC_CUSTOM_SCHEME_LABEL,
} from 'js/utils/orgTeamRubricScheme';
import { useOrgRubricSchemes } from './OrgRubricSchemesContext';
import { orgWeightResolutionKey, parentOrgIdForRow } from './orgWeightsUtils';
import styles from './OrgWeights.module.scss';

function OrgRubricSchemeRowLabel({ row, pathToNode, children }) {
  return (
    <div className={styles['weight-row']}>
      <div className={styles['weight-row-structure']}>
        <OrgWeightRowGutters pathToNode={pathToNode} />
        <div className={styles['weight-row-label']}>
          <OrgTreeNodeTypeIcon node={row} />
          <span>{row.name}</span>
        </div>
      </div>
      <div className={styles['weight-row-value']}>{children}</div>
    </div>
  );
}

function OrgRubricSchemesGrid({ organization, rubricDocument, rubricId }) {
  const { workspaceId, orgId } = useParams();
  const { teamRowById, onSchemeChange } = useOrgRubricSchemes();
  const rows = flattenOrgTreeRows(organization);
  const weightOverrides = weightOverridesSortedForPicker(rubricDocument);

  if (!organization || rows.length === 0) {
    return (
      <p className="text-muted mb-0">No organization structure to configure.</p>
    );
  }

  return (
    <div className={styles.weights}>
      <div className={styles.grid}>
        <div className={styles['grid-header']}>
          <div></div>
          <div>Weight scheme</div>
        </div>
        {rows.map((row) => {
          const resolutionKey = orgWeightResolutionKey(
            organization?.id,
            parentOrgIdForRow(row),
            row
          );

          if (isTreeRootWeightRow(row)) {
            return (
              <OrgRubricSchemeRowLabel
                key={resolutionKey || row.depthId}
                row={row}
                pathToNode={row.pathToNode}
              />
            );
          }

          if (!isEditableWeightRow(row) || row.type !== 'team') {
            return (
              <OrgRubricSchemeRowLabel
                key={resolutionKey || row.depthId}
                row={row}
                pathToNode={row.pathToNode}
              />
            );
          }

          const teamRow = teamRowById.get(row.id);
          const selectValue = selectValueForTeamRow(teamRow, rubricDocument);
          const usesNonDefaultScheme =
            isNonDefaultSchemeSelectValue(selectValue);
          const teamDefaultLabel =
            teamRow?.team_weight_scheme != null &&
            String(teamRow.team_weight_scheme).trim() !== ''
              ? ' (team default)'
              : '';

          return (
            <OrgRubricSchemeRowLabel
              key={resolutionKey || row.depthId}
              row={row}
              pathToNode={row.pathToNode}
            >
              <div className={styles['scheme-select-row']}>
                <Input
                  type="select"
                  bsSize="sm"
                  className={cx(
                    styles['weight-input'],
                    styles['scheme-select'],
                    {
                      [styles['scheme-select--non-default']]:
                        usesNonDefaultScheme,
                    }
                  )}
                  title={
                    teamRow?.org_override
                      ? `Overriding team default${teamDefaultLabel}`
                      : 'Using team rubric setting'
                  }
                  value={selectValue}
                  onChange={(ev) => onSchemeChange(row.id, ev.target.value)}
                >
                  <option value="">Default</option>
                  {weightOverrides.map((override) => {
                    const optVal = weightSchemeOptionValue(override);
                    if (!optVal) return null;
                    return (
                      <option key={optVal} value={optVal}>
                        {override?.name || optVal}
                      </option>
                    );
                  })}
                  <option value={ORG_TEAM_RUBRIC_CUSTOM_SCHEME_ID}>
                    [{ORG_TEAM_RUBRIC_CUSTOM_SCHEME_LABEL}]
                  </option>
                </Input>
                <div>
                  {isOrgTeamRubricCustomSchemeId(selectValue) && rubricId ? (
                    <Link
                      className={styles['scheme-edit-link']}
                      to={orgManageTeamCustomWeightsPath(
                        workspaceId,
                        orgId,
                        rubricId,
                        row.id
                      )}
                    >
                      Edit weights
                    </Link>
                  ) : null}
                </div>
              </div>
            </OrgRubricSchemeRowLabel>
          );
        })}
      </div>
    </div>
  );
}

export default OrgRubricSchemesGrid;
